import { createContext, useContext } from 'react'
import type { Schema } from '@/api/client'

export type User = Schema<'UserOut'>
export type Session = Schema<'SessionOut'>

/** Who this browser is, as far as the API is concerned. */
export interface Identity {
  user: User
  session: Session
}

export interface AuthValue {
  /**
   * `loading` until `/auth/me` has answered once. Rendering the app before then
   * would flash the login screen at every signed-in user on every page load.
   */
  status: 'loading' | 'anonymous' | 'authenticated'
  user: User | null
  session: Session | null
  /**
   * The session died while the app was open. Distinct from `anonymous`: the
   * user stays where they are and re-authenticates in place (the S-D2
   * behaviour), rather than losing the screen they were on.
   */
  expired: boolean
  /**
   * Why the last sign-out happened, when it was not the user's own. `idle` is
   * the fifteen-minute inactivity sign-out (`useIdleSignOut`), so the login
   * screen can say so rather than leaving a returning reader to wonder whether
   * something broke. Cleared by the next sign-in.
   */
  signedOutReason: 'idle' | null
  /** Adopt an identity returned by login, signup, or a password reset. */
  adopt: (identity: Identity) => void
  /** Re-read `/auth/me` — after selecting an organisation, or verifying an address. */
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthValue | null>(null)

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth was called outside <AuthProvider>')
  return value
}

/**
 * The signed-in user, for the screens that are only reachable behind the guard.
 *
 * Saves every one of them from a null check that the router has already made
 * impossible — and, more usefully, makes the *absence* of the guard a loud
 * failure rather than a page that renders with empty fields.
 */
export function useCurrentUser(): { user: User; session: Session } {
  const { user, session } = useAuth()
  if (!user || !session) {
    throw new Error('useCurrentUser was called outside an authenticated route')
  }
  return { user, session }
}
